import React, { FC, useEffect } from 'react';
import { Tab, usePermissions, useTranslate } from 'react-admin';
import { useQuery } from 'react-query';
import { ShowContextProvider } from 'ra-core';

import { PaperTabbedShowLayout, RecordTitle, ShowView } from '~/layout';
import { NoPermissions } from '~/components/ra';
import { ApprovalCaseDetail } from '~/types/approvalCase';
import DetailsTab from '~/resources/approvals/components/DataTab';
import ApprovalsRightPanel from '~/resources/approvals/ApprovalsRightPanel';
import CasesForObjectTab from '~/resources/approvals/components/CasesForObjectTab';
import PricingApprovalTabs from '~/resources/approvals/components/PricingApprovalTabs';
import TransactionApprovalTabs from '~/resources/approvals/components/TransactionApprovalTabs';
import { TransactionTitleHeader } from '~/resources/headTransactions/TransactionShow';
import { getTransaction } from '~/api/headTransactions';
import { getApprovalCaseFromHistory } from '~/api/approvals';
import { history } from '~/App';
import { useApprovalShowController } from '~/hooks/approvals';

const i18nResource = 'approvals';
const i18nPath = `resources.${i18nResource}`;

const TransactionApprovalHeadline: FC<{ objectId: string }> = ({ objectId }) => {
  const translate = useTranslate();
  const t = (key: string, opts?: any) => translate(`${i18nPath}.${key}`, opts);
  const { data: transaction } = useQuery(['headTransaction', objectId], () =>
    getTransaction({ id: objectId }).then((r) => r.data)
  );
  if (!transaction) return null;
  return (
    <>
      {t('show.titlePrefix')}
      <TransactionTitleHeader record={transaction} />
    </>
  );
};

const ApprovalsShowContent: FC<any> = (props) => {
  const translate = useTranslate();
  const t = (key: string, opts?: any) => translate(`${i18nPath}.${key}`, opts);
  const record = props.record as ApprovalCaseDetail | undefined;
  const isCreate = record?.actionType === 'CREATE';
  const isTransaction = record?.actionType === 'TRANSACTION';
  const isPricing =
    record?.objectType === 'PRICING' && (isCreate || record?.actionType === 'UPDATE');

  if (isTransaction && record?.objectId) {
    return <TransactionApprovalTabs {...props} />;
  }

  if (isPricing && record?.actionBody) {
    return <PricingApprovalTabs {...props} />;
  }

  return (
    <PaperTabbedShowLayout {...props}>
      <Tab label={t('tabs.data')}>
        <DetailsTab />
      </Tab>
      {!isCreate && (
        <Tab label={t('tabs.casesForObject')} path='cases'>
          <CasesForObjectTab />
        </Tab>
      )}
    </PaperTabbedShowLayout>
  );
};

const HistoryFallback: FC<{ id: string }> = ({ id }) => {
  const { data, error } = useQuery(['approvalHistory', id], () => getApprovalCaseFromHistory(id), {
    retry: false,
  });

  useEffect(() => {
    if (data) {
      history.replace(`/approvals/history/${id}/show`);
    }
  }, [data, id]);

  if (!data && !error) return null;
  if (error) return <NoPermissions />;
  return null;
};

const ApprovalsShowInner: FC<any> = (props) => {
  const translate = useTranslate();
  const t = (key: string, opts?: any) => translate(`${i18nPath}.${key}`, opts);

  const controllerProps = useApprovalShowController(props);
  const { error } = controllerProps;

  // approval case is not available for current user
  if (error?.body?.code === 'RESOURCE_NOT_AVAILABLE') {
    return <NoPermissions />;
  }

  if (error?.body?.code === 'RESOURCE_MISSING') {
    return <HistoryFallback id={props.id} />;
  }

  const title = (
    <RecordTitle
      resource={i18nResource}
      headline={(record: ApprovalCaseDetail) => {
        if (record.actionType === 'TRANSACTION' && record.objectId) {
          return <TransactionApprovalHeadline objectId={record.objectId} />;
        }
        return translate(t('show.title', { actionName: record.actionName }));
      }}
    />
  );

  return (
    <ShowContextProvider value={controllerProps}>
      <ShowView {...props} {...controllerProps} title={title} actions={null}>
        <ApprovalsShowContent />
        <ApprovalsRightPanel />
      </ShowView>
    </ShowContextProvider>
  );
};

const ApprovalsShow = (props: any) => {
  const { permissions } = usePermissions();

  if (!permissions) return null;
  if (!permissions.includes('approvalCase.view') && !permissions.includes('approvalCase.admin'))
    return <NoPermissions />;

  return <ApprovalsShowInner {...props} />;
};

export default ApprovalsShow;
